import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import * as React from "react";

import { Button } from "@/core/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/core/components/ui/command";
import { NoTranslate } from "@/core/components/ui/no-translate";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { cn } from "@/core/utils/func/cn";
import { normalizeSearchTerm } from "@/core/utils/func/searchText";

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Identifiant / référence affiché sous le libellé (UUID, code…). */
  description?: string;
  /** Si true, l’option est visible mais non sélectionnable (ex. precheck compte). */
  disabled?: boolean;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  isLoading?: boolean;
  disabled?: boolean;
  /**
   * Si true, re-cliquer l’option déjà sélectionnée vide la valeur.
   * Par défaut false : évite les boucles clear → auto-reselect (flicker / refetch).
   */
  clearable?: boolean;
  /** Entoure libellés et identifiants avec `NoTranslate` (données métier). */
  noTranslate?: boolean;
  /**
   * Comportement de largeur du popover ouvert :
   * - `"trigger"` (défaut) : width = celle du trigger (comportement historique).
   * - `"content"` : grandit avec le contenu, caps viewport, utile quand les
   *   libellés d'option sont plus longs que la largeur du trigger (grilles
   *   compactes, répéteurs multi-colonnes).
   */
  popoverWidth?: "trigger" | "content";
  id?: string;
  className?: string;
  buttonClassName?: string;
  popoverClassName?: string;
}

export function SearchableSelect({
  options = [],
  value,
  onValueChange,
  placeholder = "Select an option...",
  searchPlaceholder = "Search...",
  emptyMessage = "No options available",
  loadingMessage = "Loading...",
  isLoading = false,
  disabled = false,
  clearable = false,
  noTranslate = false,
  popoverWidth = "trigger",
  id,
  className,
  buttonClassName,
  popoverClassName,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  // Dédoublonnage défensif : des valeurs identiques produiraient des clés React
  // dupliquées et une sélection ambiguë (listes venant de l'API).
  const uniqueOptions = React.useMemo(() => {
    const seen = new Set<string>();
    return options.filter((option) => {
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
  }, [options]);

  // Filtrage manuel (cmdk désactivé) : conserve l'ordre d'origine au lieu de
  // réordonner la liste par score à chaque frappe.
  const filteredOptions = React.useMemo(() => {
    const term = normalizeSearchTerm(search);
    if (!term) return uniqueOptions;
    return uniqueOptions.filter((option) =>
      normalizeSearchTerm(
        `${option.value} ${option.label} ${option.description ?? ""}`
      ).includes(term)
    );
  }, [uniqueOptions, search]);

  const selectedOption = uniqueOptions.find((option) => option.value === value);
  // Repli sur la valeur brute : évite d'afficher le placeholder le temps que les
  // options se chargent alors que le formulaire a déjà une valeur.
  const selectedLabel = selectedOption?.label ?? value;

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "h-11 min-h-11 w-full justify-between px-3.5 text-base",
              buttonClassName
            )}
            disabled={disabled || isLoading}
          >
            <span className="truncate">
              {value
                ? noTranslate
                  ? (
                      <NoTranslate as="span">{selectedLabel}</NoTranslate>
                    )
                  : selectedLabel
                : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 w-4 h-4 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          // `max-h-none` + `overflow-y-hidden` neutralisent la hauteur dynamique et
          // le défilement du PopoverContent : seul CommandList défile. Deux
          // conteneurs imbriqués dont la hauteur dépend de
          // --radix-popover-content-available-height provoquent une boucle
          // mesure → repositionnement (bascule haut/bas visible).
          className={cn(
            "z-[80] p-0 max-h-none overflow-x-hidden overflow-y-hidden",
            popoverWidth === "trigger"
              ? "w-[var(--radix-popover-trigger-width)] max-w-[min(var(--radix-popover-trigger-width),calc(100vw-1rem))]"
              : // "content" : width mini = trigger (ne rétrécit jamais), peut
                // grandir jusqu'au viewport pour afficher les libellés en entier.
                "min-w-[var(--radix-popover-trigger-width)] w-max max-w-[calc(100vw-1rem)]",
            popoverClassName
          )}
          align="start"
          sideOffset={4}
          collisionPadding={8}
        >
          <Command shouldFilter={false}>
            {/* Le focus d'ouverture/fermeture est laissé à Radix : il cible le
                champ de recherche à l'ouverture puis rend le focus au trigger à
                la fermeture. Le FocusScope du popover met en pause celui d'une
                Dialog parente, donc aucun conflit en cas d'imbrication. */}
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder={searchPlaceholder}
              className="h-11 text-base"
            />
            {/* `vh` (statique) plutôt que la variable Radix : garde la liste
                lisible sur petit écran sans réintroduire de boucle de mesure. */}
            <CommandList className="max-h-[min(32rem,50dvh)]">
              {isLoading ? (
                <div className="flex justify-center items-center p-4">
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  {loadingMessage}
                </div>
              ) : (
                <>
                  <CommandEmpty>{emptyMessage}</CommandEmpty>
                  <CommandGroup>
                    {filteredOptions.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                        className="py-2.5 text-base"
                        onSelect={() => {
                          if (option.disabled) {
                            return;
                          }
                          if (option.value === value) {
                            if (clearable) {
                              onValueChange?.("");
                            }
                            setOpen(false);
                            return;
                          }
                          onValueChange?.(option.value);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 shrink-0",
                            value === option.value ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="flex min-w-0 flex-1 flex-col">
                          {noTranslate ? (
                            <NoTranslate as="span" className="truncate">
                              {option.label}
                            </NoTranslate>
                          ) : (
                            <span className="truncate">{option.label}</span>
                          )}
                          {option.description ? (
                            noTranslate ? (
                              <NoTranslate
                                as="span"
                                className="truncate text-xs text-muted-foreground"
                              >
                                {option.description}
                              </NoTranslate>
                            ) : (
                              <span className="truncate text-xs text-muted-foreground">
                                {option.description}
                              </span>
                            )
                          ) : null}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
